// @ts-check
// Versões e webhook de entrada na página da automação (contrato 08, seções 3.12 e 3.14): aviso do rascunho com
// "Publicar" e "Descartar", histórico de versões com "Voltar a esta versão" e o endereço do webhook com o segredo,
// o exemplo de chamada e as últimas entregas.
import { html } from 'htm/preact';
import { useEffect, useState } from 'preact/hooks';
import { Button } from '../../ui/button.js';
import { Icon } from '../../ui/icon.js';
import { Modal } from '../../ui/modal.js';
import { showErrorSnackbar, showSuccessSnackbar } from '../../ui/snackbar.js';
import {
  discardAutomationDraft, listAutomationVersions, listAutomationWebhookDeliveries, publishAutomation, regenerateAutomationWebhookSecret, restoreAutomationVersion,
} from '../../data/automations.js';
import { Tag } from '../../ui/chip.js';
import { formatDateTime } from '../../lib/format.js';
import { t } from '../../i18n/index.js';

/** @typedef {import('../../data/automations.js').Automation} Automation */

const VERSION_STATUS_COLORS = Object.freeze({ DRAFT: 'orange', PUBLISHED: 'green', ARCHIVED: 'gray' });
const DELIVERY_STATUS_COLORS = Object.freeze({ ACCEPTED: 'green', IGNORED: 'gray', UNAUTHORIZED: 'red', RATE_LIMITED: 'orange', DISABLED: 'gray', LOOP_DETECTED: 'orange', INVALID_BODY: 'red' });

/**
 * Aviso de rascunho: a versão ativa continua valendo até publicar.
 * @param {{ automation: Automation, isAdmin: boolean, onChanged: (automation: Automation) => void }} props
 */
export function DraftBanner({ automation, isAdmin, onChanged }) {
  const [isBusy, setIsBusy] = useState(false);
  if (!automation.draft) return null;
  const run = async (/** @type {() => Promise<Automation>} */ operation, /** @type {string} */ message) => {
    setIsBusy(true);
    try {
      onChanged(await operation());
      showSuccessSnackbar(message);
    } catch (error) {
      showErrorSnackbar(error);
    } finally {
      setIsBusy(false);
    }
  };
  return html`<div class="automation-draft-banner" role="status" data-testid="automation-draft-banner">
    <${Icon} name="IconPencil" size=${16} />
    <span>${t('automations.versions.draftBanner', { draft: automation.draft.number, published: automation.versionNumber ?? 1 })}</span>
    ${isAdmin
      ? html`<span class="automation-draft-actions">
          <${Button} title=${t('automations.versions.discard')} variant="secondary" size="small" disabled=${isBusy}
            onClick=${() => run(() => discardAutomationDraft(automation.id), t('automations.versions.discardedSnackbar'))} />
          <${Button} title=${t('automations.versions.publish')} icon="IconRocket" accent="blue" size="small" isLoading=${isBusy}
            onClick=${() => run(() => publishAutomation(automation.id), t('automations.versions.publishedSnackbar'))} />
        </span>`
      : null}
  </div>`;
}

/**
 * Histórico de versões: rascunho, publicada e arquivadas, com "Voltar a esta versão".
 * @param {{ automation: Automation, isAdmin: boolean, refreshKey: number, onChanged: (automation: Automation) => void }} props
 */
export function VersionHistory({ automation, isAdmin, refreshKey, onChanged }) {
  const [versions, setVersions] = useState(/** @type {import('../../data/automations.js').AutomationVersion[] | null} */ (null));
  const [confirming, setConfirming] = useState(/** @type {import('../../data/automations.js').AutomationVersion | null} */ (null));
  useEffect(() => {
    listAutomationVersions(automation.id).then(setVersions).catch(() => setVersions([]));
  }, [automation.id, automation.versionId, automation.draft?.id, automation.draft?.updatedAt, refreshKey]);
  if (!versions) return null;
  const restore = async () => {
    if (!confirming) return;
    try {
      const result = await restoreAutomationVersion(automation.id, confirming.id);
      onChanged(result.automation);
      showSuccessSnackbar(result.restoredAs === 'DRAFT' ? t('automations.versions.restoredAsDraft', { number: confirming.number }) : t('automations.versions.restoredPublished', { number: confirming.number }));
    } catch (error) {
      showErrorSnackbar(error);
    } finally {
      setConfirming(null);
    }
  };
  return html`<div class="automation-versions" data-testid="automation-versions">
    ${versions.map((version) => html`<div class="automation-version-row" key=${version.id}>
      <span class="automation-version-number">v${version.number}</span>
      <${Tag} color=${VERSION_STATUS_COLORS[version.status]} text=${t(`automations.versions.status.${version.status}`)} />
      <span class="automation-muted">${formatDateTime(version.publishedAt ?? version.updatedAt)}</span>
      <span class="automation-muted">${t('automations.versions.summary', { count: countActions(version.definition.actions), trigger: t(`automations.triggerTypes.${version.definition.triggerType}`) })}</span>
      ${isAdmin && version.status === 'ARCHIVED'
        ? html`<${Button} title=${t('automations.versions.restore')} icon="IconRestore" variant="secondary" size="small" onClick=${() => setConfirming(version)} />`
        : html`<span></span>`}
    </div>`)}
    ${confirming
      ? html`<${Modal} title=${t('automations.versions.restoreTitle', { number: confirming.number })}
          subtitle=${automation.isEnabled ? t('automations.versions.restoreSubtitleEnabled') : t('automations.versions.restoreSubtitleDisabled')}
          confirmLabel=${t('automations.versions.restore')} onClose=${() => setConfirming(null)} onConfirm=${restore} />`
      : null}
  </div>`;
}

/** @param {any[]} actions @returns {number} */
const countActions = (actions) => (actions ?? []).reduce((total, action) => total + 1 + (action.type === 'IF_ELSE' ? countActions(action.branches?.yes) + countActions(action.branches?.no) : 0), 0);

/**
 * Endereço do webhook de entrada, segredo, exemplo de chamada e as últimas entregas.
 * @param {{ automation: Automation, isAdmin: boolean, onChanged: (automation: Automation) => void }} props
 */
export function WebhookPanel({ automation, isAdmin, onChanged }) {
  const [deliveries, setDeliveries] = useState(/** @type {import('../../data/automations.js').WebhookDelivery[] | null} */ (null));
  const [isSecretVisible, setIsSecretVisible] = useState(false);
  const [isConfirmingRegenerate, setIsConfirmingRegenerate] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  useEffect(() => {
    listAutomationWebhookDeliveries(automation.id).then(setDeliveries).catch(() => setDeliveries([]));
  }, [automation.id, reloadKey]);
  const webhook = automation.webhook;
  if (!webhook) return null;
  const url = `${window.location.origin}${webhook.path}`;
  const usesApiKey = webhook.authentication === 'API_KEY';
  const authorization = usesApiKey ? 'Authorization: Bearer mm_SUA_CHAVE' : `X-Appcrm-Webhook-Secret: ${isSecretVisible ? webhook.secret : '••••••••'}`;
  const example = `curl -X POST '${url}' \\\n  -H 'Content-Type: application/json' \\\n  -H '${authorization}' \\\n  -d '${automation.triggerSettings?.expectedBody?.replace(/\s+/g, ' ') ?? '{"nome": "Maria"}'}'`;
  const copy = async (/** @type {string} */ text) => {
    try {
      await navigator.clipboard.writeText(text);
      showSuccessSnackbar(t('automations.webhook.copied'));
    } catch {
      showErrorSnackbar(t('automations.webhook.copyFailed'));
    }
  };
  const regenerate = async () => {
    try {
      onChanged(await regenerateAutomationWebhookSecret(automation.id));
      showSuccessSnackbar(t('automations.webhook.regeneratedSnackbar'));
    } catch (error) {
      showErrorSnackbar(error);
    } finally {
      setIsConfirmingRegenerate(false);
    }
  };
  return html`<div class="automation-webhook-panel" data-testid="automation-webhook-panel">
    <div class="form-field">
      <span class="form-field-label">${t('automations.webhook.url')}</span>
      <div class="automation-copy-row">
        <code class="automation-code">POST ${url}</code>
        <${Button} title=${t('automations.webhook.copy')} icon="IconCopy" variant="secondary" size="small" onClick=${() => copy(url)} />
      </div>
    </div>
    ${usesApiKey
      ? html`<p class="automation-muted">${t('automations.webhook.apiKeyHint')}</p>`
      : html`<div class="form-field">
          <span class="form-field-label">${t('automations.webhook.secret')}</span>
          <div class="automation-copy-row">
            <code class="automation-code" data-testid="webhook-secret">${isSecretVisible ? webhook.secret : '••••••••••••••••'}</code>
            <${Button} title=${isSecretVisible ? t('automations.webhook.hide') : t('automations.webhook.show')} icon=${isSecretVisible ? 'IconEyeOff' : 'IconEye'} variant="secondary" size="small"
              onClick=${() => setIsSecretVisible(!isSecretVisible)} />
            <${Button} title=${t('automations.webhook.copy')} icon="IconCopy" variant="secondary" size="small" onClick=${() => copy(webhook.secret ?? '')} />
            ${isAdmin ? html`<${Button} title=${t('automations.webhook.regenerate')} icon="IconRefresh" variant="secondary" size="small" onClick=${() => setIsConfirmingRegenerate(true)} />` : null}
          </div>
          <span class="input-hint">${t('automations.webhook.secretHint')}</span>
        </div>`}
    <details class="automation-variables">
      <summary>${t('automations.webhook.example')}</summary>
      <pre class="automation-code automation-code-block">${example}</pre>
      <p class="automation-muted">${t('automations.webhook.limitsHint')}</p>
    </details>
    <div class="form-field">
      <div class="automation-deliveries-header">
        <span class="form-field-label">${t('automations.webhook.deliveries')}</span>
        <${Button} title=${t('automations.webhook.reload')} icon="IconRefresh" variant="tertiary" size="small" onClick=${() => setReloadKey(reloadKey + 1)} />
      </div>
      ${deliveries && deliveries.length === 0 ? html`<p class="automation-muted">${t('automations.webhook.noDeliveries')}</p>` : null}
      ${(deliveries ?? []).map((delivery) => html`<div class="automation-delivery-row" key=${delivery.id}>
        <${Tag} color=${DELIVERY_STATUS_COLORS[/** @type {keyof typeof DELIVERY_STATUS_COLORS} */ (delivery.status)] ?? 'gray'} text=${t(`automations.webhook.deliveryStatus.${delivery.status}`)} />
        <span class="automation-muted">${formatDateTime(delivery.receivedAt)}</span>
        <span class="automation-muted">HTTP ${delivery.httpStatus}</span>
        <span class="automation-muted">${delivery.error ?? ''}</span>
      </div>`)}
    </div>
    ${isConfirmingRegenerate
      ? html`<${Modal} title=${t('automations.webhook.regenerateTitle')} subtitle=${t('automations.webhook.regenerateSubtitle')} confirmLabel=${t('automations.webhook.regenerate')}
          confirmAccent="danger" onClose=${() => setIsConfirmingRegenerate(false)} onConfirm=${regenerate} />`
      : null}
  </div>`;
}
